import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import {
  DataTableSelectAllChangeEvent,
  DataTableSelectionMultipleChangeEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { SelectedRowsContextType } from '../../../types/context';
import { createSelectedRowsByData } from '../../../utils/createSelectedRowsByData';
import { resolveDeselectedRowIds } from '../../../utils/resolveDeselectedRowIds';
import { resolveSelectAllSelection } from '../../../utils/resolveSelectAllSelection';

type UseSelectedRowsSlaveHandlersParams = {
  selectAllSlaveChecked: boolean;
  setSelectAllSlaveChecked: Dispatch<SetStateAction<boolean>>;
  setSelectedSlaveRows: Dispatch<SetStateAction<any[]>>;
  setSelectedSlaveVirtualRows: Dispatch<SetStateAction<number>>;
  deselectedSlaveRowIdsRef: MutableRefObject<Set<string>>;
  updateDeselectedSlaveRowIds: (ids: Set<string>) => void;
};

type UseSelectedRowsSlaveHandlersResult = Pick<
  SelectedRowsContextType,
  | 'processSelectedSlaveRowsChange'
  | 'processSelectAllSlaveChange'
  | 'resetSlaveSelectedRows'
>;

// TODO: Move slave grid management to separate context like it was done with view
export const useSelectedRowsSlaveHandlers = ({
  selectAllSlaveChecked,
  setSelectAllSlaveChecked,
  setSelectedSlaveRows,
  setSelectedSlaveVirtualRows,
  deselectedSlaveRowIdsRef,
  updateDeselectedSlaveRowIds,
}: UseSelectedRowsSlaveHandlersParams): UseSelectedRowsSlaveHandlersResult => {
  const processSelectedSlaveRowsChange = useCallback(
    (
      checkboxEvent: DataTableSelectionMultipleChangeEvent<DataTableValueArray>,
      slaveData: any[],
      slaveDatasetData: any,
      dataKey: string,
      selectedVirtualRows: number
    ) => {
      const value: Record<string, any>[] = checkboxEvent.value;

      const totalCount: number | undefined = slaveDatasetData?.totalCount;

      const isSelectAllMode: boolean =
        selectAllSlaveChecked ||
        selectedVirtualRows > 0 ||
        deselectedSlaveRowIdsRef.current.size > 0;

      if (isSelectAllMode && totalCount) {
        const nextDeselectedRowIds = resolveDeselectedRowIds(
          deselectedSlaveRowIdsRef.current,
          value,
          slaveData ?? [],
          dataKey,
          true
        );
        const selection = resolveSelectAllSelection(
          slaveData ?? [],
          totalCount,
          nextDeselectedRowIds,
          dataKey
        );
        updateDeselectedSlaveRowIds(nextDeselectedRowIds);
        setSelectAllSlaveChecked(nextDeselectedRowIds.size === 0);
        setSelectedSlaveRows(selection.selectedRows);
        setSelectedSlaveVirtualRows(selection.selectedVirtualRows);
        return;
      }

      const newSelectedRows = createSelectedRowsByData(
        value,
        slaveData ?? [],
        dataKey
      );

      if (selectAllSlaveChecked) {
        setSelectAllSlaveChecked(false);
      }

      if (newSelectedRows.length + selectedVirtualRows === totalCount) {
        setSelectedSlaveRows(value);
        setSelectAllSlaveChecked(true);
        return;
      }

      setSelectedSlaveRows(newSelectedRows);
      return;
    },
    [selectAllSlaveChecked, updateDeselectedSlaveRowIds]
  );

  const processSelectAllSlaveChange = useCallback(
    (
      checkboxEvent: DataTableSelectAllChangeEvent,
      slaveData: any[],
      slaveDatasetData: any
    ) => {
      setSelectAllSlaveChecked(checkboxEvent.checked);
      updateDeselectedSlaveRowIds(new Set<string>());

      if (checkboxEvent.checked) {
        const newSelectedRows = [...(slaveData ?? [])];
        const datasetTotalCount = slaveDatasetData?.totalCount ?? 0;

        if (newSelectedRows.length < datasetTotalCount) {
          const numberOfRowsToAdd = datasetTotalCount - newSelectedRows.length;
          setSelectedSlaveVirtualRows(numberOfRowsToAdd);
        }
        setSelectedSlaveRows(newSelectedRows);
        return;
      }

      setSelectedSlaveVirtualRows(0);
      setSelectedSlaveRows([]);
    },
    [updateDeselectedSlaveRowIds]
  );

  const resetSlaveSelectedRows = useCallback(() => {
    setSelectedSlaveRows([]);
    setSelectedSlaveVirtualRows(0);
    setSelectAllSlaveChecked(false);
    updateDeselectedSlaveRowIds(new Set<string>());
  }, [updateDeselectedSlaveRowIds]);

  return {
    processSelectedSlaveRowsChange,
    processSelectAllSlaveChange,
    resetSlaveSelectedRows,
  };
};
